import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Spin, Tooltip, message } from 'antd';
import close from '../../../../assets/RoundedClose.svg';
import done from '../../../../assets/Done, Check.svg';
import arrowIcon from '../../../../assets/arrow-right-white.svg';
import arrowLeftIcon from '../../../../assets/arrow-left.svg';
import {
    useLazyGetScoreCardPreviewQuestionaryQuery,
    useSaveScoreCardPreviewFormMutation,
    useSyncScoreCardPreviewComponentsMutation
} from '../../../../api/expenseAreas';
import { Loader } from '../../../../components/Loader/Loader';
import { ServiceComIcon } from '../../../../components/Icons/ServiceComIcon';
import InfoIcon from '../../../../components/Icons/infoIcon';
import { Section } from '../../../../api/types/expenseareas';
import { ExplanationBanner, SingleSelectScorecard } from '../../../SignUp/fields/fieldsRender';
import { QuestionAnswer } from '../../../SignUp';
import { ScoreCardSliderField } from '../../../ScoreCard/ScoreCardSliderField';
import '../../../../components/Modals/Modals.scss';
import '../../../SignUp/index.scss';
import '../../../ScoreCard/index.scss';
import './AdminScoreCardPreviewModal.scss';
import { PREVIEW_TOASTS } from './previewToastMessages';
import {
    applyQuestionaryServerErrors,
    buildFlatScoreCardSaveData,
    buildSaveDataFromExpenseArea,
    buildScoreCardPreviewSubmitBody,
    expandScorecardSectionFields,
    extractApiErrorMessage,
    extractPreviewEaPayload,
    findSaveRowForApiField,
    hasComponentScope,
    hasPreviewComponents,
    normalizeQuestionaryPayload,
    resolveScoreCardActiveSectionId,
    validateAllScoreCardSections,
    validateCurrentScoreCardSection,
    validateScoreCardAnswers
} from './scoreCardPreviewQuestionaryLogic';

interface Props {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    expenseAreaId: number;
}

const EMPTY_SECTIONS: any[] = [];
const SCORE_CARD_DESKTOP_MEDIA = '(min-width: 900px)';

function useScoreCardStepperDividerClass(): 'area__divider' | 'score-card-page__section-divider' {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(SCORE_CARD_DESKTOP_MEDIA).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(SCORE_CARD_DESKTOP_MEDIA);
        const sync = () => setIsDesktop(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    return isDesktop ? 'area__divider' : 'score-card-page__section-divider';
}

export const AdminScoreCardPreviewModal: FC<Props> = ({ openModal, setOpenModal, expenseAreaId }) => {
    const [getQuestionary, { isFetching: isLoadingQuestionary }] = useLazyGetScoreCardPreviewQuestionaryQuery();
    const [savePreview, { isLoading: isSavingPreview }] = useSaveScoreCardPreviewFormMutation();
    const [syncComponents, { isLoading: isLoadingSyncComponents }] = useSyncScoreCardPreviewComponentsMutation();

    const [previewPayload, setPreviewPayload] = useState<any>(null);
    const [activeSectionId, setActiveSectionId] = useState('');
    const [saveData, setSaveData] = useState<QuestionAnswer[]>([]);
    const [componentsSaveData, setComponentsSaveData] = useState<QuestionAnswer[]>([]);
    const [completedSectionIds, setCompletedSectionIds] = useState<Set<string>>(() => new Set());
    const [serverQuestionaryError, setServerQuestionaryError] = useState<string | null>(null);

    const stepperDividerClass = useScoreCardStepperDividerClass();

    const expenseAreaName = previewPayload?.expense_area_name ?? previewPayload?.name ?? '';
    const userName = previewPayload?.user_name ?? '';
    const hasComponentsStep = hasPreviewComponents(previewPayload);

    const formSections = useMemo(() => {
        const raw = previewPayload?.formData?.sections;
        const list = Array.isArray(raw) ? raw : EMPTY_SECTIONS;
        return list.filter((sec: Section) => !sec?.has_go_to);
    }, [previewPayload?.formData?.sections]);

    const sidebarSections = useMemo(() => {
        if (!hasComponentsStep) return formSections;

        return [
            {
                id: `components-${previewPayload.expense_area_id}`,
                title: 'Service Components'
            },
            ...formSections
        ];
    }, [formSections, hasComponentsStep, previewPayload?.expense_area_id]);

    const flatFields = useMemo(
        () => buildFlatScoreCardSaveData(formSections),
        [formSections]
    );

    const applyPayloadState = useCallback((payload: any) => {
        setPreviewPayload(payload);

        const sections = (payload?.formData?.sections ?? []).filter((sec: Section) => !sec?.has_go_to);
        const flat = buildFlatScoreCardSaveData(sections);
        setSaveData(flat);

        const completed = payload?.progressData?.completed_sections ?? [];
        setCompletedSectionIds(new Set(completed.map((id: any) => String(id))));

        const resolvedId = resolveScoreCardActiveSectionId(payload, sections);
        setActiveSectionId(resolvedId);

        if (hasPreviewComponents(payload)) {
            setComponentsSaveData(
                buildSaveDataFromExpenseArea(`components-${payload.expense_area_id}`, payload)
            );
        } else {
            setComponentsSaveData([]);
        }
    }, []);

    const loadQuestionary = useCallback(async () => {
        try {
            const res = await getQuestionary({ expense_area_id: expenseAreaId }).unwrap();
            const raw = normalizeQuestionaryPayload(res, expenseAreaId);

            if (!raw.length) {
                message.error('Preview returned no questionnaire data.');
                setPreviewPayload(null);
                return;
            }

            const payload = extractPreviewEaPayload(res, expenseAreaId);
            if (payload) {
                applyPayloadState(payload);
            }
            setServerQuestionaryError(null);
        } catch (error: unknown) {
            setPreviewPayload(null);
            message.error(extractApiErrorMessage(error, 'Could not load preview.'));
        }
    }, [applyPayloadState, expenseAreaId, getQuestionary]);

    useEffect(() => {
        if (openModal && expenseAreaId) {
            loadQuestionary();
        }
    }, [openModal, expenseAreaId, loadQuestionary]);

    useEffect(() => {
        if (!flatFields.length) return;
        setSaveData(flatFields);
    }, [previewPayload?.formData?.id, previewPayload?.formData?.updated_at, flatFields]);

    const resolvedActiveId = activeSectionId || (sidebarSections[0]?.id != null ? String(sidebarSections[0].id) : '');
    const activeIndex = sidebarSections.findIndex((s: any) => String(s.id) === resolvedActiveId);
    const activeSection = activeIndex >= 0 ? sidebarSections[activeIndex] : null;
    const isComponentsActive = resolvedActiveId.includes('components');
    const isLastSection = activeIndex >= sidebarSections.length - 1;

    const sectionStatus = (section: any, index: number) => {
        const idStr = String(section.id);
        const isActive = idStr === resolvedActiveId;
        if (isActive) return 'active' as const;
        const isDone = index < activeIndex || completedSectionIds.has(idStr);
        if (isDone) return 'done' as const;
        return 'pending' as const;
    };

    const canOpenSection = (index: number) => index <= activeIndex;

    const updateAnswer = (id: number, value: any, type: string, component_id?: number | string | null) => {
        const setter = isComponentsActive ? setComponentsSaveData : setSaveData;

        if (type === 'components') {
            setter((prev) =>
                prev.map((item) => {
                    if (item.id === id) {
                        return {
                            ...item,
                            answer: item.answer === 'true' ? 'false' : 'true',
                            error: false,
                            errorMessage: undefined
                        };
                    }
                    return item;
                })
            );
            return;
        }

        setter((prev) =>
            prev.map((item) => {
                const isMatch = hasComponentScope(component_id)
                    ? item.id === id && String(item.component_id) === String(component_id)
                    : item.id === id && item.component_id == null;

                if (!isMatch) return item;

                switch (type) {
                    case 'short-answer':
                    case 'paragraph':
                        return { ...item, answer: value, error: false, errorMessage: undefined };
                    case 'multiple-choice':
                    case 'dropdown': {
                        const arr = Array.isArray(item.answer) ? item.answer : [];
                        const updated = arr.includes(value) ? arr.filter((v: any) => v !== value) : [...arr, value];
                        return { ...item, answer: updated, error: false, errorMessage: undefined };
                    }
                    case 'single-select':
                        return {
                            ...item,
                            answer: item.answer === value ? '' : value,
                            error: false,
                            errorMessage: undefined
                        };
                    case 'slider':
                        return { ...item, answer: value, error: false, errorMessage: undefined };
                    default:
                        return item;
                }
            })
        );
    };

    const goToSection = (index: number) => {
        if (!canOpenSection(index)) return;
        setActiveSectionId(String(sidebarSections[index].id));
        setServerQuestionaryError(null);
    };

    const goToPrevSection = () => {
        if (activeIndex <= 0) return;
        setActiveSectionId(String(sidebarSections[activeIndex - 1].id));
        setServerQuestionaryError(null);
    };

    const refreshPreviewPayload = async (nextSectionId?: string) => {
        try {
            const res = await getQuestionary({ expense_area_id: expenseAreaId }).unwrap();
            const payload = extractPreviewEaPayload(res, expenseAreaId);
            if (payload) {
                applyPayloadState(payload);
                if (nextSectionId) {
                    setActiveSectionId(nextSectionId);
                }
            }
        } catch (error: unknown) {
            message.error(extractApiErrorMessage(error, 'Could not refresh preview.'));
        }
    };

    const syncComponentsStep = async () => {
        setServerQuestionaryError(null);
        const { updated, isValid } = validateScoreCardAnswers(componentsSaveData);

        if (!isValid) {
            setComponentsSaveData(updated);
            return;
        }

        try {
            await syncComponents({
                expense_area_id: expenseAreaId,
                data: {
                    selected_components: componentsSaveData
                        ?.filter((elem) => elem.answer === 'true')
                        ?.map((elem) => elem.id)
                }
            }).unwrap();

            setServerQuestionaryError(null);
            message.success(PREVIEW_TOASTS.spqChangesSaved);
            const nextId = formSections[0] ? String(formSections[0].id) : '';
            await refreshPreviewPayload(nextId);
        } catch (error: any) {
            const payload = error?.data ?? {};
            const serverErrors = payload?.errors;
            if (serverErrors !== undefined && serverErrors !== null) {
                const { next, unmapped } = applyQuestionaryServerErrors(componentsSaveData, serverErrors);
                setComponentsSaveData(next);
                setServerQuestionaryError(unmapped.length ? unmapped.join('\n') : null);
            }
            message.error(extractApiErrorMessage(error, PREVIEW_TOASTS.spqSaveComponentsFailed));
        }
    };

    const submitPreview = async () => {
        setServerQuestionaryError(null);

        const { updated, isValid } = validateAllScoreCardSections(formSections, saveData);
        if (!isValid) {
            setSaveData(updated);
            message.warning(PREVIEW_TOASTS.scoreCardCompleteRequiredFields);
            return;
        }

        try {
            await savePreview({
                expense_area_id: expenseAreaId,
                formData: buildScoreCardPreviewSubmitBody(formSections, saveData, userName)
            }).unwrap();

            setOpenModal(false);
        } catch (error: any) {
            const payload = error?.data ?? {};
            const serverErrors = payload?.errors;
            if (serverErrors !== undefined && serverErrors !== null) {
                const { next, unmapped } = applyQuestionaryServerErrors(saveData, serverErrors);
                setSaveData(next);
                setServerQuestionaryError(unmapped.length ? unmapped.join('\n') : null);
            } else {
                setServerQuestionaryError(null);
            }
            message.error(extractApiErrorMessage(error, PREVIEW_TOASTS.scoreCardSubmitFailed));
        }
    };

    const goToNextSection = () => {
        setServerQuestionaryError(null);

        const { updated, isValid } = validateCurrentScoreCardSection(saveData, activeSection);
        if (!isValid) {
            setSaveData(updated);
            return;
        }

        setCompletedSectionIds((prev) => new Set([...prev, resolvedActiveId]));
        setActiveSectionId(String(sidebarSections[activeIndex + 1].id));
    };

    async function handlePrimaryClick() {
        if (!sidebarSections.length) return;

        if (isComponentsActive) {
            await syncComponentsStep();
            return;
        }

        if (isLastSection) {
            await submitPreview();
            return;
        }

        goToNextSection();
    }

    const isSaving = isSavingPreview || isLoadingSyncComponents;
    const isInitialLoading = isLoadingQuestionary && !previewPayload;

    const titleSuffix = userName || expenseAreaName;
    const modalTitle = titleSuffix
        ? `Supplier Scorecard for ${titleSuffix}`
        : 'Supplier Scorecard Preview';

    return (
        <Modal
            className="questionary_modal admin-score-card-preview-modal"
            destroyOnClose
            centered
            open={openModal}
            onCancel={() => setOpenModal(false)}
            closeIcon={<img src={close} alt="" />}
            footer={null}
        >
            {isInitialLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                    <Spin />
                </div>
            ) : !sidebarSections.length ? (
                <div className="signup score-card-page admin-score-card-preview-signup">
                    <div className="signup__main main">
                        <div className="main__questionary questionary">
                            <div className="questionary__header">
                                <div className="questionary__title">No sections in this scorecard.</div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="signup score-card-page admin-score-card-preview-signup">
                    <div className="signup__main main">
                        <div className="main__questionary questionary">
                            <div className="questionary__header">
                                <div className="questionary__header-block-1">
                                    <div className="questionary__title">
                                        {modalTitle}{' '}
                                        <Tooltip title="Preview mode — navigate sections and submit answers to preview the score card form.">
                                            <div>
                                                <InfoIcon fill="#808CB2" />
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>

                            <div className="questionary__main">
                                <div className="questionary__EAs">
                                    <div className="questionary__area area expanded">
                                        <div className="area__sections expanded">
                                            {sidebarSections.map((section: any, index: number) => {
                                                const status = sectionStatus(section, index);
                                                const isActive = status === 'active';
                                                const isDone = status === 'done';
                                                const clickable = canOpenSection(index);

                                                return (
                                                    <Fragment key={section.id}>
                                                        <div
                                                            role={clickable ? 'button' : undefined}
                                                            tabIndex={clickable ? 0 : undefined}
                                                            aria-label={`Step ${index + 1} of ${sidebarSections.length}: ${section.title}`}
                                                            className={`area__section ${isActive ? 'active' : ''} ${
                                                                isDone ? 'done' : ''
                                                            } ${!isActive && !isDone ? 'pending' : ''} ${
                                                                clickable ? 'area__section--clickable' : ''
                                                            }`}
                                                            onClick={() => goToSection(index)}
                                                            onKeyDown={(e) => {
                                                                if (!clickable) return;
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    goToSection(index);
                                                                }
                                                            }}
                                                        >
                                                            {isDone && !isActive ? (
                                                                <div className="number_of_section">
                                                                    <img src={done} alt="" />
                                                                </div>
                                                            ) : (
                                                                <div className="number_of_section">{index + 1}</div>
                                                            )}
                                                            <span className="area__section__label">{section.title}</span>
                                                        </div>
                                                        {index !== sidebarSections.length - 1 ? (
                                                            <div className={stepperDividerClass} />
                                                        ) : null}
                                                    </Fragment>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="questionary__fields-section fields-section">
                                    {isComponentsActive ? (
                                        <div className="fields-section__components">
                                            <div className="fields-section__componentsHeader componentsHeader">
                                                <div className="componentsHeader__row">
                                                    <div className="componentsHeader__title">
                                                        <ServiceComIcon />
                                                        Service Components
                                                    </div>
                                                    <div className="componentsHeader__section-number">
                                                        Section 1 of {sidebarSections.length}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="fields-section__componentsMain componentsMain">
                                                <div className="componentsMain__title">
                                                    Select all types of service components that you have for{' '}
                                                    {expenseAreaName}:
                                                </div>
                                                <div className="componentsMain__subtitle">
                                                    You can select several options. Some of answers open an additional
                                                    section of questions
                                                </div>
                                                {serverQuestionaryError && (
                                                    <div
                                                        className="error-text"
                                                        style={{ margin: '0 0 12px 0', whiteSpace: 'pre-line' }}
                                                    >
                                                        {serverQuestionaryError}
                                                    </div>
                                                )}
                                                <div className="componentsMain__questions">
                                                    {componentsSaveData?.map((field) => (
                                                        <div className="componentsMain__item" key={field.id}>
                                                            <Checkbox
                                                                onChange={() => updateAnswer(field.id, null, 'components')}
                                                                checked={field?.answer === 'true'}
                                                                className={field?.error ? 'red' : ''}
                                                            />{' '}
                                                            {field?.name}{' '}
                                                            <Tooltip title={field?.description}>
                                                                <div>
                                                                    <InfoIcon fill="#808CB2" />
                                                                </div>
                                                            </Tooltip>
                                                            {field?.error && (
                                                                <div className="error-text" style={{ marginTop: 4 }}>
                                                                    {field.errorMessage || 'Invalid selection'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="fields-section__components">
                                            <div className="fields-section__componentsHeader componentsHeader">
                                                <div className="componentsHeader__row">
                                                    <div className="componentsHeader__title">
                                                        <ServiceComIcon />
                                                        {activeSection?.title}
                                                    </div>
                                                    <div className="componentsHeader__section-number">
                                                        Section {activeIndex + 1} of {sidebarSections.length}
                                                    </div>
                                                </div>
                                                {activeSection?.description ? (
                                                    <div className="componentsHeader__row desc">
                                                        {activeSection.description}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div>
                                                {serverQuestionaryError && (
                                                    <div
                                                        className="error-text"
                                                        style={{ margin: '0 0 12px 0', whiteSpace: 'pre-line' }}
                                                    >
                                                        {serverQuestionaryError}
                                                    </div>
                                                )}
                                                {expandScorecardSectionFields(activeSection?.fields).map(
                                                    (apiField: any) => {
                                                        const field = findSaveRowForApiField(saveData, apiField);
                                                        if (!field) return null;

                                                        const rowKey = `${field.id}-${field.component_id ?? ''}`;

                                                        if (field.type === 'single-select') {
                                                            return (
                                                                <SingleSelectScorecard
                                                                    key={rowKey}
                                                                    field={field}
                                                                    required={field.required}
                                                                    updateAnswer={updateAnswer}
                                                                    saveData={saveData}
                                                                    component={field?.component_name}
                                                                    component_id={field.component_id}
                                                                />
                                                            );
                                                        }

                                                        if (field.type === 'explanation-banner') {
                                                            return (
                                                                <ExplanationBanner
                                                                    key={rowKey}
                                                                    field={field}
                                                                    required={field.required}
                                                                    saveData={saveData}
                                                                    component_id={null}
                                                                />
                                                            );
                                                        }

                                                        if (field.type === 'slider') {
                                                            const rawAnswer = field.answer;
                                                            const sliderValue =
                                                                rawAnswer == null || Array.isArray(rawAnswer)
                                                                    ? undefined
                                                                    : typeof rawAnswer === 'string' ||
                                                                        typeof rawAnswer === 'number'
                                                                      ? rawAnswer
                                                                      : String(rawAnswer);
                                                            const sliderOptions = (field.options ?? []).map(
                                                                (o: any) => ({
                                                                    label: o.label,
                                                                    value: o.value
                                                                })
                                                            );
                                                            const sliderComponentId = hasComponentScope(
                                                                field.component_id
                                                            )
                                                                ? field.component_id
                                                                : undefined;

                                                            return (
                                                                <div className="Field score-card-field" key={rowKey}>
                                                                    {field.component_name ? (
                                                                        <div className="Field__component">
                                                                            {field.component_name}
                                                                        </div>
                                                                    ) : null}
                                                                    <ScoreCardSliderField
                                                                        title={field.name}
                                                                        description={field.description}
                                                                        options={sliderOptions}
                                                                        value={sliderValue}
                                                                        required={field.required}
                                                                        error={!!field.error}
                                                                        onSelect={(v) =>
                                                                            updateAnswer(
                                                                                field.id,
                                                                                v,
                                                                                'slider',
                                                                                sliderComponentId
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        return null;
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="questionary__btns">
                                <Button onClick={() => goToPrevSection()} disabled={activeIndex <= 0}>
                                    <div className="questionary__btn">
                                        <img src={arrowLeftIcon} alt="" />
                                        Previous
                                    </div>
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        if (isSaving) return;
                                        handlePrimaryClick();
                                    }}
                                    disabled={
                                        isComponentsActive &&
                                        (!componentsSaveData?.length ||
                                            componentsSaveData.every((elem) => elem.answer === 'false'))
                                    }
                                >
                                    <div className="questionary__btn">
                                        {isSaving ? (
                                            <Loader />
                                        ) : isLastSection && !isComponentsActive ? (
                                            'Submit Scores'
                                        ) : (
                                            'Next Step'
                                        )}{' '}
                                        <img src={arrowIcon} alt="" />
                                    </div>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};
